import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box, Typography, Grid, Chip, Avatar, CircularProgress, Button
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import PeopleIcon from "@material-ui/icons/People";
import ContactsIcon from "@material-ui/icons/Contacts";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIos from "@material-ui/icons/ArrowForwardIos";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePlanPermissions } from "../../context/PlanPermissionsContext";
import api from "../../services/api";
import { listSliderBanners } from "../../services/sliderHomeService";
import { getBackendUrl } from "../../config";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from "recharts";
import CalendarioAtendimento from "../../components/CalendarioAtendimento";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px",
    backgroundColor: theme.palette.background.default,
    minHeight: "100%",
    overflowY: "auto",
  },
  greetingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 8,
  },
  greeting: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
  companyChip: {
    backgroundColor: theme.palette.primary.main + "22",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  bannerSlider: {
    position: "relative",
    width: "100%",
    // aspect-ratio 16:9 responsivo: paddingTop como fallback
    paddingTop: "37.5%", // 300/800 ≈ 37.5% — mantém proporção em qualquer largura
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 24,
    background: "#e2e8f0",
    [theme.breakpoints.down("sm")]: {
      paddingTop: "56.25%", // 16:9 em mobile
      borderRadius: 12,
    },
  },
  bannerInner: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  bannerImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  bannerArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    transition: "all 0.2s",
    "&:hover": {
      background: "rgba(255,255,255,1)",
      transform: "translateY(-50%) scale(1.1)",
    },
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
    },
  },
  bannerArrowLeft: {
    left: 16,
    [theme.breakpoints.down("sm")]: {
      left: 8,
    },
  },
  bannerArrowRight: {
    right: 16,
    [theme.breakpoints.down("sm")]: {
      right: 8,
    },
  },
  bannerDots: {
    position: "absolute",
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 8,
    zIndex: 10,
  },
  bannerDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.5)",
    cursor: "pointer",
    transition: "all 0.2s",
    "&.active": {
      background: "#fff",
      transform: "scale(1.2)",
    },
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 14,
  },
  statCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    padding: "20px 22px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "100%",
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  filterRow: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  filterPill: {
    borderRadius: 20,
    padding: "6px 18px",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    border: `1.5px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    userSelect: "none",
  },
  filterPillActive: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    border: `1.5px solid ${theme.palette.primary.main}`,
  },
  ticketCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    padding: "18px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    height: "100%",
  },
  ticketCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  ticketCardValue: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1,
  },
  ticketCardLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  sideCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 14,
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    padding: "18px 20px",
    marginBottom: 20,
  },
  sideCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  sideCardTitle: {
    fontSize: "1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  viewAll: {
    fontSize: "0.8rem",
    color: theme.palette.primary.main,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
  },
  creditsBig: {
    fontSize: "2.2rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  creditsLabel: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginTop: 2,
  },
  kanbanItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  kanbanDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 10,
    flexShrink: 0,
  },
  kanbanName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  kanbanCount: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    borderRadius: 10,
    padding: "2px 8px",
    fontWeight: 600,
  },
  colabItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  colabName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  colabSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const TICKET_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "open", label: "Abertos" },
  { key: "pending", label: "Pendentes" },
  { key: "closed", label: "Finalizados" },
];

const TICKET_CARDS = [
  { key: "open", label: "Abertos", color: "#3b82f6", bg: "#eff6ff", Icon: HeadsetMicIcon },
  { key: "pending", label: "Pendentes", color: "#f59e0b", bg: "#fffbeb", Icon: HourglassEmptyIcon },
  { key: "closed", label: "Finalizados", color: "#22c55e", bg: "#f0fdf4", Icon: CheckCircleIcon },
  { key: "total", label: "Total", color: "#8b5cf6", bg: "#f5f3ff", Icon: AssignmentIcon },
];

const AVATAR_COLORS = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444","#14b8a6"];
const avatarColor = (name) => AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const fmtDuration = (seconds) => {
  const m = Math.floor(Number(seconds || 0) / 60);
  if (!m) return "-";
  if (m < 60) return `${m}min`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
};

const fmtCurrency = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const makeSparkline = (base, n = 7) =>
  Array.from({ length: n }, (_, i) => ({ v: Math.max(0, Math.round(base * (0.5 + 0.6 * Math.abs(Math.sin(i * 0.9))))) }));

const MONTH_NAMES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

export default function Painel() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { openAi, kanban, integrations } = usePlanPermissions();

  const [dashData, setDashData] = useState(null);
  const [chartsData, setChartsData] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);
  const [aiCredits, setAiCredits] = useState(null);
  const [crmCount, setCrmCount] = useState(0);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sliderBanners, setSliderBanners] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [d, c, f, ai, crm] = await Promise.allSettled([
        api.get("/dashboard"),
        api.get("/dashboard/charts"),
        api.get("/financeiro/dashboard"),
        api.get("/ai-credits/usage"),
        api.get("/crm/clients", { params: { pageNumber: 1, pageSize: 1 } }),
      ]);
      if (d.status === "fulfilled") setDashData(d.value.data);
      if (c.status === "fulfilled") setChartsData(c.value.data);
      if (f.status === "fulfilled") setFinanceiro(f.value.data);
      if (ai.status === "fulfilled") setAiCredits(ai.value.data);
      if (crm.status === "fulfilled") {
        const r = crm.value.data;
        setCrmCount(r.count || r.totalClientes || 0);
      }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    listSliderBanners()
      .then(res => {
        const banners = res.data || [];
        if (banners.length > 0) setSliderBanners(banners);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (sliderBanners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % sliderBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliderBanners.length]);

  const handlePrevSlide = () => {
    setCurrentSlide(prev => (prev === 0 ? sliderBanners.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % sliderBanners.length);
  };

  const buildBannerImageUrl = (banner) => {
    if (!banner) return null;
    const imagePath = banner.image || banner.imageUrl || banner.mediaPath;
    if (!imagePath) return null;
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    const backendBase = (getBackendUrl() || "").replace(/\/$/, "");
    const normalized = imagePath.replace(/\\/g, "/");
    const publicIdx = normalized.indexOf("/public/");
    let rel = publicIdx >= 0
      ? normalized.slice(publicIdx + 1)
      : normalized.replace(/^\/+/, "");
    if (!rel.startsWith("public/")) rel = `public/${rel}`;
    return backendBase ? `${backendBase}/${rel}` : `/${rel}`;
  };

  const counters = dashData?.counters || {};
  const attendants = (dashData?.attendants || []).slice(0, 10);
  const kanbanTags = (dashData?.kanbanSummary || []).slice(0, 6);
  const contactsChart = chartsData?.contactsByMonth || [];

  const totalContacts = chartsData?.totalContacts || 0;
  const newThis = chartsData?.newContactsThisMonth || 0;
  const newPrev = chartsData?.newContactsPrevMonth || 1;
  const contactTrend = newPrev > 0 ? Math.round(((newThis - newPrev) / newPrev) * 100) : 0;

  const receitaMes = financeiro?.recebidoMes || 0;

  const planLimit = aiCredits?.limit || 0;
  const tokensUsed = aiCredits?.tokensUsed || 0;
  const extraCredits = aiCredits?.extraCredits || 0;
  const planRemaining = aiCredits?.remaining || 0;

  const ticketCounts = {
    open: Number(counters.supportHappening || 0),
    pending: Number(counters.supportPending || 0),
    closed: Number(counters.supportFinished || 0),
    total: Number(counters.supportHappening || 0) + Number(counters.supportPending || 0) + Number(counters.supportFinished || 0),
  };

  const visibleCards = ticketFilter === "all"
    ? TICKET_CARDS
    : TICKET_CARDS.filter(c => c.key === ticketFilter || c.key === "total");

  return (
    <Box className={classes.root}>
      {/* Greeting */}
      <Box className={classes.greetingRow}>
        <Box>
          <Typography className={classes.greeting}>
            Olá, {user?.name || "Usuário"}!
          </Typography>
          <Typography style={{ fontSize: "0.85rem", color: theme.palette.text.secondary }}>
            Bem-vindo ao painel de controle
          </Typography>
        </Box>
        <Chip
          label={user?.companyId === 1 ? "Empresa Super Admin" : (user?.company?.name || `Empresa ID ${user?.companyId}`)}
          className={classes.companyChip}
        />
      </Box>

      <Grid container spacing={3}>
        {/* ===== ESQUERDA ===== */}
        <Grid item xs={12} lg={8}>

          {/* Banner Slider */}
          {sliderBanners.length > 0 && (
            <Box className={classes.bannerSlider}>
              {/* bannerInner ocupa 100% do espaço criado pelo paddingTop */}
              <Box className={classes.bannerInner}>
                {buildBannerImageUrl(sliderBanners[currentSlide]) ? (
                  <img
                    key={`slide-${currentSlide}`}
                    src={buildBannerImageUrl(sliderBanners[currentSlide])}
                    alt={sliderBanners[currentSlide].name || `Banner ${currentSlide + 1}`}
                    className={classes.bannerImage}
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <Box style={{
                    width: "100%", height: "100%",
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 22, fontWeight: 600,
                  }}>
                    {sliderBanners[currentSlide]?.name || "Bem-vindo ao Floow Chat"}
                  </Box>
                )}

                {sliderBanners.length > 1 && (
                  <>
                    <Box
                      className={`${classes.bannerArrow} ${classes.bannerArrowLeft}`}
                      onClick={handlePrevSlide}
                    >
                      <ArrowBackIos style={{ fontSize: 18, marginLeft: 4 }} />
                    </Box>
                    <Box
                      className={`${classes.bannerArrow} ${classes.bannerArrowRight}`}
                      onClick={handleNextSlide}
                    >
                      <ArrowForwardIos style={{ fontSize: 18 }} />
                    </Box>
                    <Box className={classes.bannerDots}>
                      {sliderBanners.map((_, idx) => (
                        <Box
                          key={idx}
                          className={`${classes.bannerDot}${idx === currentSlide ? " active" : ""}`}
                          onClick={() => setCurrentSlide(idx)}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* Dados da empresa */}
          <Typography className={classes.sectionTitle}>Dados da empresa</Typography>
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} sm={4}>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ backgroundColor: "#eff6ff" }}>
                  <PeopleIcon style={{ color: "#3b82f6", fontSize: 22 }} />
                </Box>
                <Typography className={classes.statValue}>
                  {loading ? <CircularProgress size={18} /> : crmCount.toLocaleString("pt-BR")}
                </Typography>
                <Typography className={classes.statLabel}>Clientes</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <TrendingUpIcon style={{ fontSize: 14, color: "#22c55e" }} />
                  <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "#22c55e" }}>+12%</span>
                  <span style={{ fontSize: "0.76rem", color: theme.palette.text.secondary }}>Esta semana</span>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ backgroundColor: "#fdf4ff" }}>
                  <ContactsIcon style={{ color: "#a855f7", fontSize: 22 }} />
                </Box>
                <Typography className={classes.statValue}>
                  {loading ? <CircularProgress size={18} /> : totalContacts.toLocaleString("pt-BR")}
                </Typography>
                <Typography className={classes.statLabel}>Contatos WhatsApp</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  {contactTrend >= 0
                    ? <TrendingUpIcon style={{ fontSize: 14, color: "#22c55e" }} />
                    : <TrendingDownIcon style={{ fontSize: 14, color: "#ef4444" }} />}
                  <span style={{ fontSize: "0.76rem", fontWeight: 700, color: contactTrend >= 0 ? "#22c55e" : "#ef4444" }}>
                    {contactTrend >= 0 ? "+" : ""}{contactTrend}%
                  </span>
                  <span style={{ fontSize: "0.76rem", color: theme.palette.text.secondary }}>vs mês anterior</span>
                </Box>
              </Box>
            </Grid>
            {integrations && (
              <Grid item xs={12} sm={4}>
                <Box className={classes.statCard}>
                  <Box className={classes.statIcon} style={{ backgroundColor: "#f0fdf4" }}>
                    <AttachMoneyIcon style={{ color: "#22c55e", fontSize: 22 }} />
                  </Box>
                  <Typography className={classes.statValue} style={{ fontSize: "1.35rem" }}>
                    {loading ? <CircularProgress size={18} /> : fmtCurrency(receitaMes)}
                  </Typography>
                  <Typography className={classes.statLabel}>Receita do Mês</Typography>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <TrendingUpIcon style={{ fontSize: 14, color: "#22c55e" }} />
                    <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "#22c55e" }}>+8%</span>
                    <span style={{ fontSize: "0.76rem", color: theme.palette.text.secondary }}>Este mês</span>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Tickets por Status */}
          <Box display="flex" alignItems="center" style={{ marginBottom: 14 }}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0, marginRight: 16 }}>
              Tickets em Atendimento
            </Typography>
          </Box>
          <Box className={classes.filterRow}>
            {TICKET_FILTERS.map(f => (
              <Box
                key={f.key}
                component="span"
                className={`${classes.filterPill} ${ticketFilter === f.key ? classes.filterPillActive : ""}`}
                onClick={() => setTicketFilter(f.key)}
              >
                {f.label}
              </Box>
            ))}
          </Box>
          <Grid container spacing={2}>
            {visibleCards.map(card => (
              <Grid item xs={6} sm={3} key={card.key}>
                <Box className={classes.ticketCard}>
                  <Box className={classes.ticketCardIcon} style={{ backgroundColor: card.bg }}>
                    <card.Icon style={{ color: card.color, fontSize: 20 }} />
                  </Box>
                  <Typography className={classes.ticketCardValue} style={{ color: card.color }}>
                    {loading ? "-" : ticketCounts[card.key].toLocaleString("pt-BR")}
                  </Typography>
                  <Typography className={classes.ticketCardLabel}>{card.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Calendário */}
          <Box style={{ marginTop: 24 }}>
            <CalendarioAtendimento />
          </Box>
        </Grid>

        {/* ===== DIREITA (SIDEBAR) ===== */}
        <Grid item xs={12} lg={4}>

          {/* Gráfico de Novos Contatos */}
          <Box className={classes.sideCard}>
            <Box className={classes.sideCardHeader}>
              <Typography className={classes.sideCardTitle}>Novos Contatos</Typography>
              <span style={{ fontSize: "0.78rem", color: theme.palette.text.secondary }}>últimos 12 meses</span>
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" padding={3}><CircularProgress size={24} /></Box>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={contactsChart} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ fontSize: "0.75rem", borderRadius: 8, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}
                    formatter={(v) => [v, "Contatos"]}
                  />
                  <Line type="monotone" dataKey="count" stroke={theme.palette.primary.main} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: theme.palette.primary.main }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Créditos de IA */}
          {openAi && <Box className={classes.sideCard}>
            <Box className={classes.sideCardHeader}>
              <Typography className={classes.sideCardTitle}>Créditos de IA</Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography className={classes.creditsBig}>
                  {loading ? "-" : planRemaining.toLocaleString("pt-BR")}
                </Typography>
                <Typography className={classes.creditsLabel}>Créditos do plano</Typography>
              </Box>
              <Box style={{ width: 90, height: 50 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={makeSparkline(planLimit / 10)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <Bar dataKey="v" fill={theme.palette.primary.main} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box style={{ height: 1, backgroundColor: theme.palette.divider, margin: "14px 0" }} />

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography className={classes.creditsBig} style={{ color: "#f59e0b" }}>
                  {loading ? "-" : extraCredits.toLocaleString("pt-BR")}
                </Typography>
                <Typography className={classes.creditsLabel}>Créditos avulsos</Typography>
              </Box>
              <Box style={{ width: 90, height: 50 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={makeSparkline(Math.max(extraCredits / 5, 5))} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <Line type="monotone" dataKey="v" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box style={{ marginTop: 12, backgroundColor: theme.palette.action.hover, borderRadius: 10, padding: "8px 12px" }}>
              <Typography style={{ fontSize: "0.78rem", color: theme.palette.text.secondary }}>
                Usados: <strong style={{ color: theme.palette.text.primary }}>{tokensUsed.toLocaleString("pt-BR")}</strong>
                {" / "}
                Limite: <strong style={{ color: theme.palette.text.primary }}>{planLimit.toLocaleString("pt-BR")}</strong>
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              onClick={() => history.push("/creditos-ia")}
              style={{ marginTop: 12, borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              Comprar mais créditos
            </Button>
          </Box>}

          {/* Tags Kanban */}
          {kanban && <Box className={classes.sideCard}>
            <Box className={classes.sideCardHeader}>
              <Typography className={classes.sideCardTitle}>Tags Kanban</Typography>
              <a href="/kanban" className={classes.viewAll}>Ver Todos &rsaquo;</a>
            </Box>
            {loading ? (
              <CircularProgress size={20} />
            ) : kanbanTags.length === 0 ? (
              <Typography style={{ fontSize: "0.82rem", color: theme.palette.text.secondary }}>Nenhuma tag Kanban configurada</Typography>
            ) : kanbanTags.map(tag => (
              <Box key={tag.id} className={classes.kanbanItem}>
                <Box display="flex" alignItems="center">
                  <Box className={classes.kanbanDot} style={{ backgroundColor: tag.color || theme.palette.primary.main }} />
                  <Typography className={classes.kanbanName}>{tag.name}</Typography>
                </Box>
                <span className={classes.kanbanCount}>{tag.ticketsCount} tickets</span>
              </Box>
            ))}
          </Box>}

          {/* Top 5 Colaboradores */}
          <Box className={classes.sideCard} style={{ marginBottom: 0 }}>
            <Box className={classes.sideCardHeader}>
              <Typography className={classes.sideCardTitle}>Top 5 Colaboradores</Typography>
              <a href="/relatorios" className={classes.viewAll}>Ver Todos &rsaquo;</a>
            </Box>
            {loading ? (
              <CircularProgress size={20} />
            ) : attendants.slice(0, 5).length === 0 ? (
              <Typography style={{ fontSize: "0.82rem", color: theme.palette.text.secondary }}>Sem dados</Typography>
            ) : attendants.slice(0, 5).map(a => (
              <Box key={a.id} className={classes.colabItem}>
                <Box display="flex" alignItems="center">
                  <Avatar
                    style={{ width: 36, height: 36, marginRight: 10, fontSize: "0.85rem", fontWeight: 700, backgroundColor: avatarColor(a.name) }}
                  >
                    {(a.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography className={classes.colabName}>{a.name}</Typography>
                    <Typography className={classes.colabSub}>
                      {Number(a.tickets || 0)} tickets &middot; ★ {Number(a.rating || 0).toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="span"
                  style={{
                    border: `1.5px solid ${a.online ? "#22c55e" : theme.palette.divider}`,
                    borderRadius: 20,
                    padding: "3px 12px",
                    fontSize: "0.73rem",
                    fontWeight: 600,
                    color: a.online ? "#22c55e" : theme.palette.text.secondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.online ? "Online" : "Offline"}
                </Box>
              </Box>
            ))}
          </Box>

        </Grid>
      </Grid>
    </Box>
  );
}
